import { MessageDescriptor } from "react-intl";

import EntityType from "@mapmycustomers/shared/enum/EntityType";
import { ChatRecordUpdateAction } from "@mapmycustomers/shared/types/chat/ChatContent";
import Deal from "@mapmycustomers/shared/types/entity/Deal";
import Funnel from "@mapmycustomers/shared/types/entity/deals/Funnel";
import Stage from "@mapmycustomers/shared/types/entity/deals/Stage";

import dealLockMessages, {
  moveIntoLockedFunnelMessage,
  moveIntoReadOnlyStageMessage,
} from "@app/util/deal/dealLockMessages";
import getDealLock, { canApplyDealEdit, LockableDeal } from "@app/util/deal/getDealLock";

import { readIdOf } from "./createRecordSchema";
import { storedFunnelOf } from "./proposalDealLocks";
import { resolveStage } from "./proposalStage";
import { readDraft } from "./recordUpdateDraft";

const STAGE_FIELDS = new Set(["funnel", "funnelId", "stage", "stageId"]);

const readStageId = ({ stage, stageId }: Record<string, unknown>): Stage["id"] | undefined => {
  if (typeof stageId === "number") {
    return stageId;
  }
  return typeof stage === "number" ? stage : readIdOf(stage);
};

// The lock note a record_update on a deal is blocked with, or undefined. The deal's own lock needs
// its looked-up funnel and stage (dealRefs; until then the server is the backstop); a move into a
// read-only stage or a locked funnel is judged from the stored stages alone.
const resolveRecordUpdateDealLock = (
  action: ChatRecordUpdateAction,
  draft: Record<string, unknown> | undefined,
  funnelStages: Record<Funnel["id"], Stage[]>,
  dealRefs: Record<Deal["id"], LockableDeal>
): MessageDescriptor | undefined => {
  if (action.entityType !== EntityType.DEAL) {
    return undefined;
  }
  const ref: LockableDeal | undefined = dealRefs[action.entityId];
  const values = {
    ...Object.fromEntries(action.changes.map(({ field, proposedValue }) => [field, proposedValue])),
    ...readDraft(draft)?.values,
  };
  const lock = ref ? getDealLock(ref, storedFunnelOf(ref, funnelStages), funnelStages) : undefined;
  const changesStage = Object.keys(values).some((field) => STAGE_FIELDS.has(field));
  if (lock && !canApplyDealEdit(lock, changesStage) && lock.reason) {
    return dealLockMessages[lock.reason];
  }
  const stageId = readStageId(values);
  const target =
    stageId === undefined
      ? undefined
      : resolveStage(funnelStages, undefined, { id: stageId, name: "" });
  if (target?.dealsReadOnly) {
    return moveIntoReadOnlyStageMessage;
  }
  return target?.funnel?.stageMovesLocked ? moveIntoLockedFunnelMessage : undefined;
};

export default resolveRecordUpdateDealLock;
